import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type AgentKind = 'onboarding' | 'compliance' | 'policy' | 'comp' | 'offer' | 'qa';

export interface AgentResult {
  answer: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
  citedRecords: string[];
  resultId: string;
}

export interface AiSavedResult {
  id: string;
  agent_kind: AgentKind;
  title: string;
  input_text: string;
  output_text: string;
  model: string | null;
  cited_record_ids: string[];
  pinned: boolean;
  created_at: string;
}

export interface AiRun {
  id: string;
  agent_kind: AgentKind;
  model: string;
  prompt_chars: number | null;
  completion_chars: number | null;
  cost_cents: number | null;
  cited_record_ids: string[] | null;
  used_own_key: boolean;
  created_at: string;
}

export interface RunAgentInput {
  agentKind: AgentKind;
  question: string;
  /** Server-side hint only — no model name exposed to the client. */
  reasoning?: boolean;
  countryContext?: string;
  /**
   * Optional attached document — PDF, DOCX, TXT/MD, or PNG/JPG.
   * When present we POST as multipart/form-data; the backend parses
   * the file to text server-side and prepends it to the question.
   */
  file?: File;
}

export function useRunAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RunAgentInput) => {
      if (input.file) {
        const fd = new FormData();
        fd.append('agentKind', input.agentKind);
        fd.append('question', input.question);
        if (input.reasoning != null) fd.append('reasoning', String(input.reasoning));
        if (input.countryContext) fd.append('countryContext', input.countryContext);
        fd.append('file', input.file);
        // No explicit Content-Type — axios derives the multipart
        // boundary from the FormData body. Setting it manually here
        // would omit the boundary and break multer on the server.
        const r = await api.post<AgentResult>('/api/ai/run', fd);
        return r.data;
      }
      const { file: _file, ...json } = input;
      void _file;
      const r = await api.post<AgentResult>('/api/ai/run', json);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai'] }),
  });
}

export function useAiRuns(agent?: AgentKind) {
  return useQuery({
    queryKey: ['ai', 'runs', agent ?? 'all'],
    queryFn: async () => {
      const r = await api.get<{ runs: AiRun[] }>('/api/ai/runs', { params: agent ? { agent } : {} });
      return r.data.runs;
    },
  });
}

export function useAiSpend() {
  return useQuery({
    queryKey: ['ai', 'spend'],
    queryFn: async () => {
      const r = await api.get<{ spentCents: number }>('/api/ai/spend');
      return r.data;
    },
    refetchInterval: 60_000,
  });
}

const AGENT_LABEL: Record<AgentKind, { en: string; ja: string; hint: string }> = {
  onboarding: { en: 'Onboarding Assistant', ja: 'オンボーディング',  hint: 'Guides new hires through the country-appropriate documents' },
  compliance: { en: 'Compliance Q&A',       ja: 'コンプライアンス Q&A', hint: 'Cites authoritative sources (厚生労働省 / IRS / HMRC / BMF)' },
  policy:     { en: 'Policy Drafter',       ja: '社内規程ドラフト',    hint: 'Drafts 就業規則 sections, employee handbooks, works-council notices' },
  comp:       { en: 'Comp Analyzer',        ja: '報酬分析',           hint: 'Benchmarks against market + internal cohorts (admin-only)' },
  offer:      { en: 'Offer Letter Writer',  ja: 'オファーレター作成',  hint: 'Composes localized offer letters' },
  qa:         { en: 'HR Q&A',               ja: '人事 Q&A',           hint: 'Open-ended; reads employees + regulations + policies' },
};

export function agentLabel(kind: AgentKind, locale: string): { name: string; hint: string } {
  const meta = AGENT_LABEL[kind];
  return { name: locale.startsWith('ja') ? meta.ja : meta.en, hint: meta.hint };
}

// ── Saved AI results (Phase 11) — list / reopen / pin / delete ──

export function useAiResults(agent?: AgentKind) {
  return useQuery({
    queryKey: ['ai', 'results', agent ?? 'all'],
    queryFn: async () => {
      const r = await api.get<{ results: AiSavedResult[] }>('/api/ai/results', { params: agent ? { agent } : {} });
      return r.data.results;
    },
  });
}

export function usePinAiResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      await api.post(`/api/ai/results/${id}/pin`, { pinned });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'results'] }),
  });
}

export function useDeleteAiResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/ai/results/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'results'] }),
  });
}
